import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredUserType?: "investor" | "project-holder";
  allowPending?: boolean; // Allow access even if user status is pending_approval
}

const ProtectedRoute = ({ children, requiredUserType, allowPending = false }: ProtectedRouteProps) => {
  const { user, userType, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        // If user is not authenticated, redirect to login
        navigate("/login");
      } else if (!isAdmin && requiredUserType && userType !== requiredUserType) {
        // If user is not admin and doesn't have the required user type, redirect to login
        navigate("/login");
      } else if (!allowPending && user.status !== "approved") {
        // If user status is not approved and pending is not allowed, redirect to waiting approval page
        if (userType === "investor") {
          navigate("/waiting-approval/investor");
        } else if (userType === "project-holder") {
          navigate("/waiting-approval/project-holder");
        } else {
          navigate("/login");
        }
      }
    }
  }, [user, userType, isAdmin, loading, navigate, requiredUserType, allowPending]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If user is authenticated and either:
  // 1. Is admin (can access any dashboard), OR
  // 2. Has the required user type (if specified), AND
  // 3. Status is approved or pending is allowed
  if (user && 
      (isAdmin || !requiredUserType || userType === requiredUserType) && 
      (allowPending || user.status === "approved")) {
    return <>{children}</>;
  }

  // This shouldn't be reached due to the redirect above, but just in case
  return null;
};

export default ProtectedRoute;